/**
 * An EyeMed login's code state — the one definition of what a login doc says
 * and how it changes (openspec/changes/eyemed-mfa-email-codes, design D2).
 *
 * A login is one EyeMed username, however many clinics save it
 * (`eyemedLogins/{loginId}`, owned by functions/src/portalCodes). Whether it
 * is set up and whether it is paused are independent facts: a set-up login can
 * pause, and a login can pause before it is ever set up. Every writer — the
 * setup callables, the inbox, the robot and staff — applies the transitions
 * below inside its own transaction, so two writers can never pause a login
 * twice, alert twice or lift a pause nobody fixed.
 *
 * Pure: no Firestore and no firebase-functions, because the scrape worker
 * loads it. Timestamps on the doc are ISO strings; transitions take `now`.
 */

/** How long the robot waits for a code before it pauses the login; also the
 *  age at which an open request is a dead run. */
export const CODE_WAIT_MS = 4 * 60 * 1000;
/** A setup counts as open while its popup polled within this long. */
export const SETUP_OPEN_MS = 2 * 60 * 1000;
/** How far back `recentCodeAt` reaches, and the code alert's window. */
export const CODE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** The code count inside the window that alerts our team. */
export const CODE_ALERT_COUNT = 3;

/** What the clinic is told when its admin has the next step. */
export const EYEMED_SETUP_NEEDED_MESSAGE = "EyeMed code setup needed";
/** What the clinic is told when the pause is ours to fix. */
export const EYEMED_VITALREV_FIXING_MESSAGE = "VitalRev is fixing EyeMed checks";

export type PauseOwner = "admin" | "vitalrev";

/** Our address is not on the EyeMed user: the admin's setup lifts these. */
export const ADMIN_PAUSE_CAUSES = ["address_not_offered", "no_address"] as const;
/** A fault on our side: a staff restart lifts these. */
export const VITALREV_PAUSE_CAUSES = [
  "no_email", "email_without_code", "code_rejected", "run_ended",
] as const;

export type AdminPauseCause = typeof ADMIN_PAUSE_CAUSES[number];
export type VitalrevPauseCause = typeof VITALREV_PAUSE_CAUSES[number];
export type PauseCause = AdminPauseCause | VitalrevPauseCause;
/** How a code request ended: completed, or the cause of the pause it made. */
export type RequestEnding = "completed" | PauseCause;

export function isPauseCause(value: unknown): value is PauseCause {
  return (ADMIN_PAUSE_CAUSES as readonly unknown[]).includes(value) ||
    (VITALREV_PAUSE_CAUSES as readonly unknown[]).includes(value);
}

export function pauseOwnerOf(cause: PauseCause): PauseOwner {
  return (ADMIN_PAUSE_CAUSES as readonly string[]).includes(cause) ? "admin" : "vitalrev";
}

export function messageForOwner(owner: PauseOwner): string {
  return owner === "admin" ? EYEMED_SETUP_NEEDED_MESSAGE : EYEMED_VITALREV_FIXING_MESSAGE;
}

export interface EyemedLoginPause {
  owner: PauseOwner;
  cause: PauseCause;
  pausedAt: string;
  /** The code request that ended in the pause; null when none was open. */
  requestId: string | null;
  checkId: string | null;
}

export interface EyemedLoginSetup {
  openedAt: string;
  /** The popup's last poll — the setup is open while this is recent. */
  lastSeenAt: string;
  /** The email whose code set the login up, or lifted its pause, while this
   *  setup was open; the popup shows its code while it is kept. */
  confirmedEmailId: string | null;
}

export interface EyemedOpenRequest {
  id: string;
  checkId: string;
  requestedAt: string;
}

export interface EyemedLoginDoc {
  /** As first saved, trimmed. */
  username: string;
  /** The practice whose clinic first needed the doc (opened its setup, or
   *  whose check first met a code screen); its address is on that practice's
   *  domain. */
  ownerOrgId: string;
  /** Null until made; never changes once made. */
  address: string | null;
  setUpAt: string | null;
  pause: EyemedLoginPause | null;
  setup: EyemedLoginSetup | null;
  openRequest: EyemedOpenRequest | null;
  /** The code arrivals of the last seven days. */
  recentCodeAt: string[];
  codeAlertAt: string | null;
  createdAt: string;
}

/** The fields a transition changes, written with `update` (a nested map is
 *  replaced whole). */
export type EyemedLoginPatch = Partial<Pick<
  EyemedLoginDoc,
  "address" | "setUpAt" | "pause" | "setup" | "openRequest" | "recentCodeAt" | "codeAlertAt"
>>;

export type EyemedLoginState =
  | "not_set_up"
  | "set_up"
  | "paused_admin"
  | "paused_vitalrev";

export type EyemedLoginReadiness =
  | {ready: true; state: "not_set_up" | "set_up"}
  | {
    ready: false;
    state: "paused_admin" | "paused_vitalrev";
    owner: PauseOwner;
    message: string;
  };

/** A login doc as first written: not set up, not paused, no address. */
export function newEyemedLogin(input: {
  username: string; ownerOrgId: string; now: Date;
}): EyemedLoginDoc {
  return {
    username: input.username.trim(),
    ownerOrgId: input.ownerOrgId,
    address: null,
    setUpAt: null,
    pause: null,
    setup: null,
    openRequest: null,
    recentCodeAt: [],
    codeAlertAt: null,
    createdAt: input.now.toISOString(),
  };
}

/**
 * A login's state. No doc reads as not set up: nothing writes one until an
 * admin opens the login's setup or the robot meets a code screen. A pause
 * wins over set-up.
 */
export function loginState(doc: EyemedLoginDoc | null): EyemedLoginState {
  if (!doc) return "not_set_up";
  if (doc.pause?.owner === "admin") return "paused_admin";
  if (doc.pause) return "paused_vitalrev";
  return doc.setUpAt ? "set_up" : "not_set_up";
}

/** Whether the robot may run a check of this login: every login runs until
 *  it is paused, set up or not (design D3). When paused, whose next step it
 *  is and what the clinic is told. */
export function loginReadiness(doc: EyemedLoginDoc | null): EyemedLoginReadiness {
  const state = loginState(doc);
  switch (state) {
    case "not_set_up":
    case "set_up":
      return {ready: true, state};
    case "paused_admin":
      return {ready: false, state, owner: "admin", message: EYEMED_SETUP_NEEDED_MESSAGE};
    case "paused_vitalrev":
      return {ready: false, state, owner: "vitalrev", message: EYEMED_VITALREV_FIXING_MESSAGE};
  }
}

function ageMs(iso: string | null | undefined, now: Date): number {
  const at = typeof iso === "string" ? Date.parse(iso) : NaN;
  return Number.isFinite(at) ? now.getTime() - at : Infinity;
}

/** A setup is open while its popup polled within `SETUP_OPEN_MS`. */
export function isSetupOpen(doc: EyemedLoginDoc, now: Date): boolean {
  return !!doc.setup && ageMs(doc.setup.lastSeenAt, now) <= SETUP_OPEN_MS;
}

/**
 * A code reached the login's address. It sets up a login that isn't, lifts
 * an admin's pause, and lifts a VitalRev pause only while a setup is open — a
 * late code from a wait that timed out, or one a person triggered by signing
 * in with the robot's EyeMed user, restarts nothing nobody fixed. While a
 * setup is open, the email that set the login up or lifted its pause is that
 * setup's confirmation. Every code counts toward the alert: the third inside
 * seven days stamps `codeAlertAt`, unless one was stamped in the last seven.
 */
export function onCodeArrived(
  doc: EyemedLoginDoc,
  input: {emailId: string; now: Date},
): EyemedLoginPatch {
  const {emailId, now} = input;
  const nowIso = now.toISOString();
  const recentCodeAt = (doc.recentCodeAt ?? [])
    .filter((at) => ageMs(at, now) < CODE_WINDOW_MS)
    .concat(nowIso);
  const patch: EyemedLoginPatch = {recentCodeAt};
  if (recentCodeAt.length >= CODE_ALERT_COUNT && !(ageMs(doc.codeAlertAt, now) < CODE_WINDOW_MS)) {
    patch.codeAlertAt = nowIso;
  }

  const setupOpen = isSetupOpen(doc, now);
  let changed = false;
  if (!doc.setUpAt) {
    patch.setUpAt = nowIso;
    changed = true;
  }
  if (doc.pause && (doc.pause.owner === "admin" || setupOpen)) {
    patch.pause = null;
    changed = true;
  }
  if (changed && setupOpen && doc.setup) {
    patch.setup = {...doc.setup, confirmedEmailId: emailId};
  }
  return patch;
}

/**
 * Pause the login for `cause`, closing its open request. Null when the login
 * is already paused: the first writer wins, and only it alerts.
 */
export function pauseLogin(
  doc: EyemedLoginDoc,
  input: {cause: PauseCause; requestId: string | null; checkId: string | null; now: Date},
): EyemedLoginPatch | null {
  if (doc.pause) return null;
  return {
    pause: {
      owner: pauseOwnerOf(input.cause),
      cause: input.cause,
      pausedAt: input.now.toISOString(),
      requestId: input.requestId,
      checkId: input.checkId,
    },
    openRequest: null,
  };
}

/** Start a setup. Null for a login that is set up and not paused: there is
 *  nothing for an admin to do. */
export function openSetup(doc: EyemedLoginDoc, now: Date): EyemedLoginPatch | null {
  if (doc.setUpAt && !doc.pause) return null;
  const nowIso = now.toISOString();
  return {setup: {openedAt: nowIso, lastSeenAt: nowIso, confirmedEmailId: null}};
}

/** The popup polled: the setup stays open. Null when no setup was opened. */
export function pollSetup(doc: EyemedLoginDoc, now: Date): EyemedLoginPatch | null {
  if (!doc.setup) return null;
  return {setup: {...doc.setup, lastSeenAt: now.toISOString()}};
}

/**
 * The admin confirmed "Yes, I put it into EyeMed": our address is on the
 * EyeMed user. EyeMed sends no code when a user's email changes, so the
 * admin's word is taken: the login is set up, and a pause that is the
 * admin's is lifted, so the robot meets the next code request with our
 * address in place. Null for a login with no address, or paused as
 * VitalRev's, whose pause only staff lift.
 */
export function confirmAddress(doc: EyemedLoginDoc | null, now: Date): EyemedLoginPatch | null {
  if (!doc?.address || doc.pause?.owner === "vitalrev") return null;
  return {
    setUpAt: doc.setUpAt ?? now.toISOString(),
    ...(doc.pause ? {pause: null} : {}),
  };
}

/**
 * A staff restart. Only a VitalRev pause, and only the one staff saw
 * (`pausedAt`): a pause lifted meanwhile, or a new one since, is refused.
 */
export function restartLogin(
  doc: EyemedLoginDoc | null,
  input: {pausedAt: string},
): EyemedLoginPatch | null {
  if (doc?.pause?.owner !== "vitalrev" || doc.pause.pausedAt !== input.pausedAt) return null;
  return {pause: null};
}

/** The robot met a code screen: the request is open until it ends. */
export function openCodeRequest(
  input: {id: string; checkId: string; now: Date},
): EyemedLoginPatch {
  return {openRequest: {id: input.id, checkId: input.checkId, requestedAt: input.now.toISOString()}};
}

/** The request completed. Null when it is no longer the open one. */
export function completeCodeRequest(doc: EyemedLoginDoc, requestId: string): EyemedLoginPatch | null {
  return doc.openRequest?.id === requestId ? {openRequest: null} : null;
}

/**
 * An open request left by a run that died: one from this same check (the
 * platform retrying a task whose run ended mid-wait), or one older than the
 * wait itself. Logging in again would make EyeMed send another code, so the
 * caller closes it as `run_ended` and pauses the login instead.
 */
export function isDeadRequest(
  open: EyemedOpenRequest | null,
  input: {checkId: string; now: Date},
): boolean {
  if (!open) return false;
  return open.checkId === input.checkId || !(ageMs(open.requestedAt, input.now) <= CODE_WAIT_MS);
}
